import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import {
  ArrowLeft, FileText, Shield, Heart, Users, Sparkles, TrendingUp,
  Brain, AlertOctagon, UserCheck, CreditCard, Ban, MessageSquare,
  Link as LinkIcon, Lock, Copyright, AlertTriangle, Scale, FileCheck,
  Trash2, RefreshCw, Gavel, Mail,
} from 'lucide-react';

const Section = ({ icon: Icon, number, title, children, accent = 'stone' }) => {
  const accentMap = {
    stone:   'bg-stone-100 text-stone-700',
    rose:    'bg-rose-100 text-rose-700',
    sky:     'bg-sky-100 text-sky-700',
    amber:   'bg-amber-100 text-amber-700',
    emerald: 'bg-emerald-100 text-emerald-700',
    violet:  'bg-violet-100 text-violet-700',
    red:     'bg-red-100 text-red-700',
    indigo:  'bg-indigo-100 text-indigo-700',
    blue:    'bg-blue-100 text-blue-700',
  };
  return (
    <Card className="p-6 md:p-8 bg-white border-stone-200 shadow-sm">
      <div className="flex items-start gap-4 mb-4">
        <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${accentMap[accent]}`}>
          <Icon className="w-6 h-6" strokeWidth={1.6} />
        </div>
        <div className="mt-1">
          <p className="text-xs font-medium text-stone-400 tracking-wider uppercase">Section {number}</p>
          <h2 className="text-xl md:text-2xl font-nunito font-bold text-[#2D2D2D]">
            {title}
          </h2>
        </div>
      </div>
      <div className="text-[#57534E] leading-relaxed space-y-3 text-[15px]">
        {children}
      </div>
    </Card>
  );
};

const Bullets = ({ items }) => (
  <ul className="list-disc list-outside pl-5 space-y-1 marker:text-stone-400">
    {items.map((item, i) => (
      <li key={i}>{item}</li>
    ))}
  </ul>
);

const TermsOfServicePage = () => {
  const navigate = useNavigate();
  const lastUpdated = 'May 25, 2026';

  return (
    <div className="min-h-screen bg-[#FAFAF9]" data-testid="terms-of-service-page">
      <div className="max-w-4xl mx-auto px-6 py-12">
        <Button
          onClick={() => navigate(-1)}
          variant="ghost"
          className="mb-8 text-primary hover:text-primary/80"
          data-testid="back-button"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back
        </Button>

        {/* Hero */}
        <div className="bg-gradient-to-br from-primary/10 to-secondary/10 rounded-xl p-8 mb-8 border border-stone-200">
          <div className="flex items-center gap-4 mb-4">
            <FileText className="w-12 h-12 text-primary" strokeWidth={1.5} />
            <div>
              <h1 className="text-3xl md:text-4xl font-nunito font-bold text-[#2D2D2D]">
                Terms of Service
              </h1>
              <p className="text-[#57534E]">Last Updated: {lastUpdated}</p>
            </div>
          </div>
          <div className="text-[#57534E] leading-relaxed space-y-2">
            <p>
              Please read these Terms of Service (&ldquo;Terms&rdquo;) carefully before using Calm Pathways
              (&ldquo;Calm Pathways,&rdquo; &ldquo;we,&rdquo; &ldquo;our,&rdquo; or &ldquo;us&rdquo;).
            </p>
            <p>
              By accessing or using the Calm Pathways website, mobile application, services, features, content,
              and related technologies (collectively, the &ldquo;Service&rdquo;), you agree to be bound by these Terms
              and our Privacy Policy.
            </p>
            <p className="font-medium text-[#2D2D2D]">
              If you do not agree to these Terms, you may not use the Service.
            </p>
          </div>
        </div>

        <div className="space-y-6">
          <Section icon={FileCheck} number="1" title="Nature of the Service" accent="sky">
            <p>
              Calm Pathways is a consumer wellness, educational, and organizational platform designed for parents,
              guardians, and caregivers navigating complex neuroimmune-related experiences and wellness tracking.
            </p>
            <p>The Service may include:</p>
            <Bullets items={[
              'educational resources,',
              'symptom tracking,',
              'food and supplement logs,',
              'medication tracking,',
              'AI-assisted organizational tools,',
              'wellness insights,',
              'community features,',
              'reports and exports,',
              'meal planning tools,',
              'and related organizational functionality.',
            ]} />
            <p>The Service is intended solely for informational, educational, organizational, and wellness-support purposes.</p>
            <p>The Service is not:</p>
            <Bullets items={[
              'a medical provider,',
              'healthcare provider,',
              'emergency service,',
              'diagnostic tool,',
              'clinical treatment platform,',
              'or medical device.',
            ]} />
            <p>
              Unless explicitly stated otherwise in a separate written agreement, Calm Pathways may not be subject
              to HIPAA in all circumstances.
            </p>
          </Section>

          <Section icon={Users} number="2" title="Parent & Caregiver Use Only" accent="sky">
            <p>The Service is intended for use by:</p>
            <Bullets items={[
              'parents,',
              'legal guardians,',
              'caregivers,',
              'and authorized adults age 18 or older.',
            ]} />
            <p>Children do not independently create accounts or directly operate the Service.</p>
            <p>Parents and caregivers are solely responsible for:</p>
            <Bullets items={[
              'information entered into the Service;',
              'supervision of child-related care decisions;',
              'maintaining account confidentiality;',
              'and ensuring they have authority to provide information related to children entered into the Service.',
            ]} />
          </Section>

          <Section icon={Heart} number="3" title="Medical & Educational Disclaimer" accent="rose">
            <p>Calm Pathways is an educational and organizational tool only.</p>
            <p>The Service does not provide:</p>
            <Bullets items={[
              'medical advice,',
              'psychiatric advice,',
              'diagnosis,',
              'treatment,',
              'therapy,',
              'clinical decision-making,',
              'medication recommendations,',
              'supplement prescriptions,',
              'or emergency intervention.',
            ]} />
            <p>Nothing within the Service should be interpreted as:</p>
            <Bullets items={[
              'individualized medical guidance,',
              'treatment recommendations,',
              'diagnostic conclusions,',
              'emergency instructions,',
              'or clinical supervision.',
            ]} />
            <p>
              All healthcare decisions should be made in consultation with licensed healthcare professionals
              familiar with the child&rsquo;s individual circumstances and medical history.
            </p>
            <p>Use of the Service does not create:</p>
            <Bullets items={[
              'a provider-patient relationship,',
              'therapist-patient relationship,',
              'physician-patient relationship,',
              'or any other healthcare relationship.',
            ]} />
          </Section>

          <Section icon={Sparkles} number="4" title="AI Features & Automated Content" accent="violet">
            <p>
              Certain features within the Service may utilize artificial intelligence (&ldquo;AI&rdquo;) technologies to generate:
            </p>
            <Bullets items={[
              'educational summaries,',
              'organizational insights,',
              'trends,',
              'informational responses,',
              'reports,',
              'correlations,',
              'or wellness-related observations.',
            ]} />
            <p>You acknowledge and agree that:</p>
            <Bullets items={[
              'AI-generated content may be inaccurate, incomplete, outdated, biased, or inappropriate;',
              'AI outputs are generated automatically and are not reviewed by licensed healthcare professionals;',
              'AI-generated information should never be relied upon for diagnosis, treatment decisions, medication changes, supplement decisions, or emergency situations.',
            ]} />
            <p>
              Parents and caregivers are solely responsible for independently reviewing and verifying information
              with qualified healthcare professionals.
            </p>
          </Section>

          <Section icon={TrendingUp} number="5" title="Tracking, Correlations & Wellness Insights" accent="emerald">
            <p>The Service may allow users to organize and track:</p>
            <Bullets items={[
              'symptoms,', 'behaviors,', 'emotional states,', 'foods,', 'supplements,',
              'medications,', 'environmental observations,', 'infections,', 'routines,',
              'and other wellness-related information.',
            ]} />
            <p>The Service may generate:</p>
            <Bullets items={[
              'trends,', 'correlations,', 'summaries,', 'visualizations,',
              'or organizational insights.',
            ]} />
            <p>These features are provided solely for informational and organizational purposes.</p>
            <p>Any trends, correlations, or insights generated by the Service:</p>
            <Bullets items={[
              'do not establish causation,',
              'do not constitute diagnosis,',
              'do not confirm triggers,',
              'and should not be interpreted as treatment recommendations or clinical conclusions.',
            ]} />
            <p className="font-semibold text-[#2D2D2D]">Correlation does not establish medical causation.</p>
            <p>
              Generated insights depend on information entered by users and may be incomplete, inaccurate, or
              misleading if entered information is incomplete or incorrect.
            </p>
          </Section>

          <Section icon={Brain} number="6" title="Evolving Research & Medical Perspectives" accent="indigo">
            <p>Certain conditions, symptoms, and wellness topics discussed within the Service involve:</p>
            <Bullets items={[
              'evolving scientific research,',
              'differing clinical perspectives,',
              'ongoing medical debate,',
              'and emerging areas of study.',
            ]} />
            <p>Educational materials within the Service may reference:</p>
            <Bullets items={[
              'theories,',
              'hypotheses,',
              'clinical observations,',
              'or commonly discussed perspectives',
            ]} />
            <p>that are not universally accepted within the medical community.</p>
            <p>
              The inclusion of such information does not constitute endorsement, proof of effectiveness, or
              established medical consensus.
            </p>
          </Section>

          <Section icon={AlertOctagon} number="7" title="No Emergency or Crisis Support" accent="red">
            <p className="font-bold text-red-700 uppercase tracking-wide">
              The Service is not monitored for emergencies.
            </p>
            <p>Calm Pathways does not provide:</p>
            <Bullets items={[
              'crisis intervention,',
              'suicide prevention services,',
              'emergency monitoring,',
              'urgent medical review,',
              'or real-time healthcare supervision.',
            ]} />
            <p>
              Entries, logs, uploaded information, AI interactions, reports, messages, and community posts are not
              continuously monitored for emergencies or safety concerns.
            </p>
            <p>The Service should never be used in place of:</p>
            <Bullets items={[
              'emergency services,',
              'crisis support,',
              'emergency psychiatric care,',
              'or urgent medical evaluation.',
            ]} />
            <p>
              If a child may be at risk of harm to self or others, or is experiencing a medical or psychiatric
              emergency, immediately contact:
            </p>
            <Bullets items={[
              '911 or local emergency services,',
              'emergency medical personnel,',
              'the child’s physician,',
              'or an appropriate crisis service.',
            ]} />
          </Section>

          <Section icon={UserCheck} number="8" title="User Accounts" accent="sky">
            <p>To use certain features of the Service, you may be required to create an account.</p>
            <p>You agree to:</p>
            <Bullets items={[
              'provide accurate and current information;',
              'maintain confidentiality of your account credentials;',
              'promptly notify us of unauthorized account access;',
              'and accept responsibility for activities occurring under your account.',
            ]} />
            <p>We reserve the right to suspend or terminate accounts for:</p>
            <Bullets items={[
              'violations of these Terms;',
              'harmful conduct;',
              'misuse of AI or community features;',
              'fraud;',
              'abuse of the Service;',
              'or any conduct we determine may harm users, third parties, or the Service.',
            ]} />
          </Section>

          <Section icon={CreditCard} number="9" title="Subscription & Billing" accent="amber">
            <div>
              <h3 className="font-semibold text-[#2D2D2D] mb-2">Billing</h3>
              <p>Subscription fees may be charged on a monthly or annual basis.</p>
              <p>By subscribing, you authorize recurring charges to your selected payment method.</p>
            </div>
            <div>
              <h3 className="font-semibold text-[#2D2D2D] mt-3 mb-2">Cancellation</h3>
              <p>You may cancel your subscription at any time.</p>
              <p>Access to paid features will generally continue until the end of the current billing cycle.</p>
            </div>
            <div>
              <h3 className="font-semibold text-[#2D2D2D] mt-3 mb-2">Pricing Changes</h3>
              <p>We reserve the right to modify pricing with reasonable prior notice.</p>
              <p>
                Continued use of the Service after pricing changes become effective constitutes acceptance of
                updated pricing.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-[#2D2D2D] mt-3 mb-2">Refunds</h3>
              <p>Except where required by law, subscription fees are generally non-refundable.</p>
            </div>
          </Section>

          <Section icon={Ban} number="10" title="Acceptable Use" accent="red">
            <p>You agree NOT to:</p>
            <Bullets items={[
              'use the Service for unlawful purposes;',
              'share account credentials;',
              'attempt unauthorized access to systems or data;',
              'misuse AI-generated content;',
              'post harmful, abusive, or inappropriate content;',
              'harass other users;',
              'scrape, reverse engineer, or data mine the Service;',
              'interfere with operation or security of the Service;',
              'impersonate others;',
              'or use the Service to provide medical advice to third parties.',
            ]} />
          </Section>

          <Section icon={MessageSquare} number="11" title="Community Features" accent="emerald">
            <p>If community or peer-support features are available:</p>
            <Bullets items={[
              'content shared by users reflects personal experiences and opinions only;',
              'user-generated content is not reviewed for medical accuracy;',
              'Calm Pathways does not endorse, verify, or guarantee statements made by users.',
            ]} />
            <p>Users are responsible for information they choose to share publicly.</p>
            <p>We reserve the right to remove content or restrict access at our discretion.</p>
          </Section>

          <Section icon={LinkIcon} number="12" title="Third-Party Services & External Content" accent="indigo">
            <p>
              The Service may rely on third-party providers and may include links to external websites, resources,
              or services.
            </p>
            <p>Third-party providers may include:</p>
            <Bullets items={[
              'payment processors,',
              'AI service providers,',
              'cloud infrastructure providers,',
              'analytics providers,',
              'app store platforms,',
              'and external content sources.',
            ]} />
            <p>We do not control and are not responsible for:</p>
            <Bullets items={[
              'third-party availability,',
              'accuracy,',
              'security,',
              'privacy practices,',
              'interruptions,',
              'or content.',
            ]} />
            <p>Accessing third-party content or services is done at your own discretion and risk.</p>
          </Section>

          <Section icon={Lock} number="13" title="Privacy" accent="stone">
            <p>
              Your use of the Service is also governed by our Privacy Policy, which is incorporated into these
              Terms by reference.
            </p>
            <p>By using the Service, you acknowledge and agree that:</p>
            <Bullets items={[
              'information may be stored and processed electronically;',
              'no method of electronic transmission or storage can be guaranteed completely secure;',
              'and use of the Service occurs at your own discretion and risk.',
            ]} />
          </Section>

          <Section icon={Copyright} number="14" title="Intellectual Property" accent="violet">
            <p>
              The Service and all associated content, software, features, branding, and functionality are owned
              by Calm Pathways and protected by applicable intellectual property laws.
            </p>
            <p>You may not:</p>
            <Bullets items={[
              'reproduce,',
              'modify,',
              'distribute,',
              'reverse engineer,',
              'exploit,',
              'or commercially use',
            ]} />
            <p>the Service or its content without prior written permission.</p>
          </Section>

          <Section icon={AlertTriangle} number="15" title="Disclaimer of Warranties" accent="amber">
            <p className="font-semibold text-[#2D2D2D] uppercase tracking-wide">
              The Service is provided &ldquo;as is&rdquo; and &ldquo;as available.&rdquo;
            </p>
            <p>To the maximum extent permitted by law, we disclaim all warranties, including:</p>
            <Bullets items={[
              'MERCHANTABILITY;',
              'FITNESS FOR A PARTICULAR PURPOSE;',
              'NON-INFRINGEMENT;',
              'ACCURACY;',
              'RELIABILITY;',
              'AVAILABILITY.',
            ]} />
            <p>We do not guarantee:</p>
            <Bullets items={[
              'uninterrupted operation;',
              'error-free functionality;',
              'preservation of data;',
              'accuracy of generated insights;',
              'or specific outcomes from use of the Service.',
            ]} />
          </Section>

          <Section icon={Scale} number="16" title="Limitation of Liability" accent="red">
            <p className="font-semibold text-[#2D2D2D] uppercase tracking-wide">
              To the maximum extent permitted by law, Calm Pathways shall not be liable for:
            </p>
            <Bullets items={[
              'indirect damages,',
              'consequential damages,',
              'incidental damages,',
              'special damages,',
              'loss of data,',
              'emotional distress,',
              'health-related outcomes,',
              'reliance on AI-generated content,',
              'or decisions made using information from the Service.',
            ]} />
            <p>Use of the Service and reliance on any information provided is at your sole discretion and risk.</p>
            <p className="font-semibold text-[#2D2D2D] uppercase tracking-wide">
              In no event shall our total liability exceed the amount paid by you to the Service during the
              twelve (12) months preceding the claim.
            </p>
          </Section>

          <Section icon={Shield} number="17" title="Indemnification" accent="stone">
            <p>
              You agree to defend, indemnify, and hold harmless Calm Pathways and its affiliates, officers,
              employees, contractors, licensors, and agents from claims, liabilities, damages, losses, or
              expenses arising from:
            </p>
            <Bullets items={[
              'your use of the Service;',
              'your violation of these Terms;',
              'your submitted content;',
              'misuse of AI-generated information;',
              'or violation of applicable laws or third-party rights.',
            ]} />
          </Section>

          <Section icon={Gavel} number="18" title="Arbitration & Class Action Waiver" accent="indigo">
            <p>
              Any dispute arising from these Terms or the Service shall be resolved through binding arbitration
              in the State of South Dakota.
            </p>
            <p>You agree:</p>
            <Bullets items={[
              'to waive the right to a jury trial;',
              'and to waive participation in class actions or class arbitration.',
            ]} />
            <p>Claims must be brought individually.</p>
          </Section>

          <Section icon={Trash2} number="19" title="Account Termination & Deletion" accent="rose">
            <p>You may discontinue use of the Service or request account deletion at any time.</p>
            <p>
              We may suspend or terminate access to the Service for violations of these Terms or for operational,
              security, or safety reasons.
            </p>
            <p>
              Upon verified deletion requests, we will delete or de-identify personal information within a
              commercially reasonable timeframe, except where retention is necessary for:
            </p>
            <Bullets items={[
              'legal obligations,',
              'fraud prevention,',
              'security purposes,',
              'backups,',
              'or enforcement of agreements.',
            ]} />
          </Section>

          <Section icon={RefreshCw} number="20" title="Changes to the Terms" accent="stone">
            <p>We may update these Terms periodically.</p>
            <p>Material changes may be communicated through:</p>
            <Bullets items={[
              'app notifications,',
              'email,',
              'website notices,',
              'or updated posting dates.',
            ]} />
            <p>Continued use of the Service after changes become effective constitutes acceptance of the revised Terms.</p>
          </Section>

          <Section icon={Scale} number="21" title="Governing Law" accent="indigo">
            <p>
              These Terms shall be governed by the laws of the State of South Dakota, without regard to conflict
              of law principles.
            </p>
            <p>Any legal proceedings not subject to arbitration shall occur exclusively within South Dakota.</p>
          </Section>

          <Section icon={Mail} number="22" title="Contact Information" accent="stone">
            <p>If you have questions regarding these Terms, please contact:</p>
            <div className="bg-stone-50 border border-stone-200 rounded-lg p-4 mt-2">
              <p className="font-semibold text-[#2D2D2D]">Me and My PANDAS LLC</p>
              <p>
                Email:{' '}
                <a
                  href="mailto:contact@meandmypandas.com"
                  className="text-primary hover:underline font-medium"
                >
                  contact@meandmypandas.com
                </a>
              </p>
            </div>
          </Section>
        </div>

        <div className="mt-10 p-6 bg-stone-50 border border-stone-200 rounded-xl text-center">
          <Shield className="w-8 h-8 text-stone-500 mx-auto mb-2" />
          <p className="text-sm text-[#57534E]">
            Last Updated: {lastUpdated} · By using Calm Pathways, you acknowledge that you have read,
            understood, and agree to these Terms of Service.
          </p>
        </div>
      </div>
    </div>
  );
};

export default TermsOfServicePage;
